<!-- @format -->

<!-- TODO:  Migrate Order Confirmation Ticketmaster to order module
1. Move all order confirmation sub components in modules/order/TheOrderConfirmation
2. To maintain backwards compatibility, I use this component just for MMH for now
3. OrderConfirmationPaymentInfo should have 2 variants. Now we have OrderConfirmationPaymentInfo and PaymentSummary.
4. TheOrderConfirmation and OrderConfirmationPaymentInfo were copied from view/cart and adapted to MMH flow as well
-->
<template>
    <div class="gst-order-confirmation d-flex">
        <OrderConfirmationRokt
            v-if="isHotelOnlyFlow"
            :order="order" />
        <div class="gst-order-confirmation__content d-flex">
            <div class="gst-order-confirmation__header-container d-flex">
                <span class="title">{{ title }}</span>
                <span
                    v-if="isExperienceOffer && !hotelReservationOrderNumber"
                    class="subtitle">
                    {{ $t('_common:terms.order') }} #{{ eventTicketOrderNumber }}
                </span>
                <template v-else>
                    <span
                        v-if="hasTickets"
                        class="subtitle">
                        {{ $t('ticketsOrder') }} #{{ eventTicketOrderNumber }}
                    </span>
                    <span
                        v-if="hasHotelReservation"
                        class="subtitle hotel-number">
                        {{ $t('hotelOrder') }} #{{ hotelReservationOrderNumber }}
                    </span>
                </template>
            </div>
            <OrderConfirmationExperienceInfo
                v-if="isExperienceOffer"
                v-bind="propsExperience" />
            <template v-else>
                <OrderConfirmationTicketsInfo
                    v-if="hasTickets"
                    :event="event"
                    :ticket="ticket" />
                <HotelReservationInfo
                    v-if="hasHotelReservation"
                    :event-location="eventLocation"
                    :is-package-offer="isPackageOffer"
                    :has-hotel-reservation-only="hasHotelReservationOnly"
                    :gift-card="giftCard"
                    :currency="hotelReservationInfo.currency"
                    :hotel-rating="hotelReservationInfo.award"
                    :hotel-name="hotelReservationInfo.name"
                    :hotel-image="hotelReservationInfo.image"
                    :hotel-address="hotelReservationInfo.address"
                    :hotel-accommodation="hotelReservationInfo.accommodation"
                    :hotel-room-rate="hotelReservationInfo.basePrice"
                    :hotel-supplier-confirmation-number="
                        hotelReservationInfo.supplierConfirmationNumber
                    "
                    class="gst-hotel-reservation-info--display-v2">
                    <template
                        v-if="isManageBookingFlow"
                        #cta>
                        <BaseButton
                            class="gst-btn gst-btn__manage-booking mt-4 mt-md-3"
                            :placeholder="
                                $t('main:modules.hotel.features.manageBooking.terms.manageMyHotel')
                            "
                            color="primary"
                            @click="onClickManageMyHotelDo" />
                    </template>
                </HotelReservationInfo>
            </template>
            <!-- TODO: Should have similar names OrderConfirmationPaymentInfo and PaymentSummary
                OrderConfirmationPaymentInfo has more details and used on normal checkout flow
                PaymentSummary is a lighter component used on MMH flow
            -->
            <OrderConfirmationPaymentInfo
                v-if="!isManageBookingFlow"
                :currency="currency"
                :payment-items="paymentItems"
                :has-hotel-reservation-only="hasHotelReservationOnly"
                :order="order"
                :is-package-offer="isPackageOffer"
                :is-open-inventory-or-dynamic="isOpenInventoryOrDynamic"
                class="gst-order-confirmation__payment-info" />
            <PaymentSummary
                v-else
                :currency="currency"
                :payment-items="paymentItems"
                :fees="fees"
                :has-hotel-reservation-only="true"
                :total-included-fee-amount="0"
                :total-pay-later-amount="totalPayLaterAmount"
                :total-base-price="0"
                :total-price="0"
                :show-expandable-details="false"
                :fees-hotels-not-included-enum="feesHotelsNotIncludedEnum"
                class="gst-order-confirmation__payment-info" />
            <div
                v-if="showGoMobileSection"
                class="gst-order-confirmation__go-mobile d-flex">
                <MobileTicket />
                <span>
                    <strong>{{ $t('goMobile') }}</strong>
                    {{ $t('goMobileInfo') }}
                </span>
            </div>
            <BaseButton
                v-if="hasTickets"
                class="gst-btn"
                :data-test-id="$testId('button.buyAgain')"
                @click="onClickBuyAgainDo">
                {{ $t('buyAgainButton.title') }}
            </BaseButton>
            <div class="gst-order-confirmation__things-to-know d-flex">
                <div class="title">
                    {{ $t('thingsToKnow') }}
                </div>
                <CheckoutNotes
                    v-if="!!checkoutNotes"
                    class="mt-7"
                    :header="checkoutNotes.data.package_flows__event_info_header"
                    :content="checkoutNotes.data.package_flows__event_info_content" />
                <div
                    class="manage-account-container d-flex"
                    tabindex="0"
                    @click="onClickManageAccountDo"
                    @keypress.enter="onClickManageAccountDo">
                    <div class="manage-account d-flex">
                        <span>{{ $t('manageYourAccount') }}</span>
                        <span><ChevronRight /></span>
                    </div>
                    <div class="help-center d-flex">
                        {{ $t('helpCenter') }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import productsConstants from '@core/utils/constants/products';
import offerConstants from '@core/utils/constants/offer';
import feeConstants from '@core/utils/constants/fee';
import { getRouteToPoint } from '@core/utils/locationMapUtils';
import { isMobileTicket } from '@core/utils/shippingOptionsUtils';
import purchaseConstants from '@tenant/app/utils/constants/purchase';
import EventFlowMixin from '@tenant/app/mixins/EventFlowMixin';
import FeesContainerMixin from '@tenant/app/views/cart/TheCheckout/Mixins/FeesContainerMixin';
import { openTicketMasterLoginPage } from '@tenant/app/utils/tenantUrlslUtils';
import HtmlHeadMixin from '@tenant/app/mixins/HtmlHeadMixin';
import ChevronRight from '@tenant/app/assets/icons/chevron_right.svg';
import MobileTicket from '@tenant/app/assets/icons/mobile_ticket.svg';
import OrderConfirmationTicketsInfo from '@tenant/app/views/cart/TheOrderConfirmation/OrderConfirmationTicketsInfo.vue';
import OrderConfirmationPaymentInfo from '@tenant/app/views/cart/TheOrderConfirmation/OrderConfirmationPaymentInfo.vue';
import OrderConfirmationRokt from '@tenant/app/views/cart/TheOrderConfirmation/OrderConfirmationRokt.vue';
import OrderConfirmationExperienceInfo from '@tenant/app/views/cart/TheOrderConfirmation/OrderConfirmationExperienceInfo.vue';
import CheckoutNotes from '@tenant/app/views/cart/TheCheckout/Containers/CheckoutNotes.vue';
import PaymentSummary from '@tenant/app/modules/order/components/PaymentSummary.vue';
import HotelReservationInfo from '@tenant/app/modules/order/components/HotelReservationInfo.vue';

export default {
    name: 'OrderConfirmation',
    components: {
        MobileTicket,
        ChevronRight,
        HotelReservationInfo,
        OrderConfirmationTicketsInfo,
        OrderConfirmationPaymentInfo,
        OrderConfirmationRokt,
        OrderConfirmationExperienceInfo,
        CheckoutNotes,
        PaymentSummary,
    },
    mixins: [EventFlowMixin, HtmlHeadMixin, FeesContainerMixin],
    i18nOptions: {
        namespaces: 'main',
        keyPrefix: 'views.cart.theOrderConfirmation',
    },
    testIdOptions: {
        keyPrefix: 'orderConfirmation',
    },
    computed: {
        computedCartId() {
            return this.cartId || this.$route.params.id;
        },
        routeParams() {
            return this.$route.params;
        },
        order() {
            return this.routeParams.order;
        },
        checkoutNotes() {
            return this.routeParams.checkoutNotes;
        },
        legacyEventId() {
            return this.routeParams.legacyEventId;
        },
        purchaseFlow() {
            return this.routeParams.purchaseFlow;
        },
        isOpenInventoryOrDynamic() {
            return (
                this.purchaseFlow === purchaseConstants.CART.FLOWS.HOTELS_OPEN_INVENTORY ||
                this.purchaseFlow === purchaseConstants.CART.FLOWS.DYNAMIC
            );
        },
        eventTicket() {
            return this.order.items.find(
                (item) => item.productTypeId === productsConstants.TYPES.TICKET
            );
        },
        eventTicketItems() {
            return this.eventTicket.offers;
        },
        eventTicketOrderNumber() {
            return this.eventTicket.orderNumber;
        },
        hotelReservation() {
            return this.order.items.find(
                (item) => item.productTypeId === productsConstants.TYPES.HOTEL_RESERVATION
            );
        },
        hasHotelReservation() {
            return !!this.hotelReservation;
        },
        hasHotelReservationOnly() {
            return this.hasHotelReservation && this.order.items.length === 1;
        },
        hotelReservationOrderNumber() {
            return this.hotelReservation.orderNumber;
        },
        hasTickets() {
            return !!this.eventTicket;
        },
        packageOffer() {
            return this.order.packageOffer;
        },
        isExperienceOffer() {
            return (
                this.isPackageOffer &&
                this.packageOffer.type === offerConstants.TYPES.EXPERIENCES_PACKAGE
            );
        },
        title() {
            if (this.isExperienceOffer) {
                return this.$t('experienceTitle');
            }
            if (this.hasHotelReservationOnly) {
                return this.$t('hotelOnlyTitle');
            }
            return this.$t('hotelAndTicketsTitle');
        },
        event() {
            if (!this.hasTickets) {
                return null;
            }
            const {
                name,
                imageUrl: image,
                startDateTime,
                endDateTime,
                venueName,
                address,
                hasStartTime,
            } = this.eventTicket.event;
            const directionsUrl = getRouteToPoint({
                street: address.street,
                cityName: address.cityName,
                stateCode: address.stateCode,
                countryCode: address.countryCode,
            });

            return {
                name,
                image,
                startDateTime,
                endDateTime,
                hasStartTime,
                venueName,
                location: {
                    ...address,
                },
                directionsUrl,
            };
        },
        eventLocation() {
            return (
                this.event && {
                    street: this.event.location.address,
                    cityName: this.event.location.cityName,
                    stateCode: this.event.location.stateCode,
                    countryCode: this.event.location.countryCode,
                }
            );
        },
        ticket() {
            if (!this.hasTickets) {
                return false;
            }
            const { eventTicket } = this;
            const { ticket } = eventTicket;
            const { row, section, seats } = ticket;

            return {
                packageOfferId: eventTicket.packageOfferId,
                redemptionUrl: eventTicket.redemptionUrl,
                seat: {
                    row,
                    section,
                    seats,
                },
            };
        },
        hotelReservationInfo() {
            if (!this.hasHotelReservation) {
                return null;
            }

            const { hotelReservation } = this;
            const { accommodation, hotel } = hotelReservation;
            const { room } = hotel;

            return {
                name: hotel.name,
                address: hotel.address,
                image: hotel.image,
                accommodation: accommodation,
                roomCancellationPolicy: room.rate.cancellationPolicies,
                roomAmenities: hotel.room.amenities,
                award: hotelReservation.hotel.award,
                currency: this.order.currency,
                roomRate: hotelReservation.basePrice,
                accommodationNights: hotelReservation.accommodation.nights,
                packageOffer: this.packageOffer,
                supplierConfirmationNumber: hotelReservation.supplierConfirmationNumber,
            };
        },
        currency() {
            return this.order.currency;
        },
        paymentItems() {
            let ret = [];

            if (this.hasTickets) {
                const { eventTicketItems } = this;

                const tickets = eventTicketItems.reduce((acc, item) => {
                    acc = [
                        ...acc,
                        {
                            name: item.name,
                            quantity: item.quantity,
                        },
                    ];

                    return acc;
                }, []);

                ret = [...ret, ...tickets];
            }
            if (this.hasHotelReservation) {
                const { hotelReservation } = this;

                ret.push({
                    name: hotelReservation.hotel.room.name.toLowerCase(),
                    quantity: hotelReservation.accommodation.roomsCount,
                });
            }

            return ret;
        },
        showGoMobileSection() {
            if (!this.hasTickets) {
                return false;
            }
            const deliveryInfo =
                this.eventTicket.charges.find((item) => item.type === feeConstants.TYPES.DELIVERY)
                    .info || {};

            return isMobileTicket(deliveryInfo);
        },
        isPackageOffer() {
            return this.ticket && !!this.ticket.packageOfferId;
        },
        propsExperience() {
            const { hotelReservation, eventTicket, packageOffer } = this;
            const { hotel } = hotelReservation;
            const { event } = eventTicket;
            const { address } = event;
            const { billingDetails } = this.order;
            const directionsUrl = getRouteToPoint({
                street: address.address,
                cityName: address.cityName,
                stateCode: address.stateCode,
                countryCode: address.countryCode,
            });

            return {
                name: packageOffer.name,
                imageUrl: packageOffer.imageUrl,
                highlights: packageOffer.highlights,
                hotel: {
                    name: hotel.name,
                    award: hotel.award,
                },
                hotelReservation: {
                    accommodation: hotelReservation.accommodation,
                    room: {
                        name: hotelReservation.hotel.room.name,
                    },
                    orderNumber: hotelReservation.orderNumber,
                },
                event: {
                    address: {
                        venueName: event.venueName,
                        street: address.street,
                        city: address.cityName,
                        stateCode: address.stateCode,
                        country: address.countryCode,
                    },
                    startDate: event.startDateTime,
                    endDate: event.endDateTime,
                    startDateHasTime: event.hasStartTime,
                    directionsUrl,
                },
                eventTicket: eventTicket,
                billingDetails,
            };
        },
        giftCard() {
            return this.order.giftCard;
        },
    },
    methods: {
        ...mapActions({
            updateLanguagesForEvent: 'languages/updateLanguagesForEvent',
        }),
        onClickManageAccountDo() {
            openTicketMasterLoginPage();
        },
        onClickBuyAgainDo() {
            if (this.isExperienceOffer) {
                this.$router.push({
                    name: 'eventMarketing',
                    params: {
                        id: this.legacyEventId,
                    },
                });
            } else {
                this.$router.push({
                    name: 'eventPackageOffer',
                    params: {
                        id: this.eventTicket.event.id,
                    },
                });
            }
        },
        onClickManageMyHotelDo() {
            this.$router.replace({
                name: 'booking',
                params: { id: this.computedCartId },
            });
        },
    },
    mounted() {
        if (!this.order) {
            this.$router.back();
            return;
        }
        this.updateDocumentTitle(
            this.$t('main:viewsTitle.theEvent.experiences', {
                package: this.hotelReservation.hotel.name,
            })
        );
        if (this.eventTicket) {
            this.updateLanguagesForEvent(this.eventTicket.legacyEventId);
        }
    },
};
</script>

<style lang="scss">
@import '@scssMixins';
@import '@scssVariables';

.gst-order-confirmation {
    background-color: theme-color('white');
    color: theme-color('quaternary');
    flex-direction: column;
    align-items: center;

    .gst-btn {
        height: 46px !important;
        width: 80%;
        background-color: theme-color('primary') !important;
        color: theme-color('white') !important;
        text-transform: unset !important;
    }

    .gst-order-confirmation__content {
        padding: 0 20%;
        flex-direction: column;
        align-items: center;
        overflow-y: scroll;
    }

    .gst-order-confirmation__header-container {
        width: 100%;
        padding: 44px;
        margin-top: theme-spacing(7);
        background: #0b309a;
        color: white;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        .title {
            line-height: 43px !important;
            margin-bottom: theme-spacing(3);
            font-size: 34px !important;
            font-weight: font-weight('bold');
            font-family: 'AmericanSans', sans-serif !important;
            text-align: center;
        }

        .subtitle {
            line-height: line-height('xxl') !important;
            font-size: font-size('m') !important;
            font-weight: font-weight('bold');
            font-family: 'AmericanSans', sans-serif !important;
            text-align: center;
        }

        .hotel-number {
            margin-top: theme-spacing(2);
        }
    }

    .gst-order-confirmation__payment-info {
        width: 80%;
        margin-top: theme-spacing(10);
    }

    .gst-order-confirmation__go-mobile {
        width: 80%;
        line-height: line-height('xs');
        padding: theme-spacing(4) theme-spacing(6);
        background: theme-color('quinary');
        color: theme-color('quaternary');
        font-size: font-size('xxxs');
        border-radius: 1px;
        align-items: center;

        span {
            margin-left: theme-spacing(3);
        }
    }

    .gst-order-confirmation__things-to-know {
        width: 80%;
        margin-top: theme-spacing(10);
        margin-bottom: theme-spacing(8);
        flex-direction: column;

        .title {
            line-height: line-height('xxl') !important;
            padding: theme-spacing(4);
            background: linear-gradient(
                270deg,
                theme-color-hex('gradient-primary-1') 0%,
                theme-color-hex('gradient-primary-2') 100%
            );
            color: white;
            font-size: font-size('l') !important;
            font-weight: font-weight('bold');
            font-family: 'AmericanSans', sans-serif !important;
        }

        .manage-account-container {
            padding: theme-spacing(4);
            border: 1px solid theme-color('senary');
            flex-direction: column;
            border-top: 0;

            .manage-account {
                line-height: line-height('m');
                padding-bottom: theme-spacing(3);
                color: #000000;
                font-size: font-size('xxs');
                align-items: center;
                justify-content: space-between;
                border-bottom: 1px solid theme-color('senary');
                cursor: pointer;
            }

            .help-center {
                line-height: line-height('m');
                padding: theme-spacing(5) 0 theme-spacing(1);
                color: theme-color('primary');
                font-size: font-size('xxs');
                font-weight: font-weight('bold');
                align-items: center;
                justify-content: center;
            }
        }
    }

    img {
        width: 100%;
    }
}

@include mobile-only {
    .gst-order-confirmation {
        .gst-order-confirmation__content {
            padding: 0 !important;
        }

        .gst-btn {
            height: 46px;
            width: 95%;
            background-color: theme-color('primary') !important;
            color: theme-color('white') !important;
        }

        .gst-order-confirmation__header-container {
            margin-top: theme-spacing(2);

            .title {
                line-height: 30px !important;
                font-size: 24px !important;
            }

            .subtitle {
                line-height: 17px !important;
                font-size: 14px !important;
            }
        }

        .gst-order-confirmation__payment-info,
        .gst-order-confirmation__go-mobile,
        .gst-order-confirmation__things-to-know {
            width: 95% !important;
        }
    }
}
</style>
